import { FC } from 'react';
import { Users } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { InternalOverviewResponse } from '../../types/internal';
import { InternalKPICard, InternalChartCard, InternalTableCard, InternalSectionHeader, InternalStatusBadge, InternalProgressRow } from './index';
import { formatNumber, CHART_COLORS } from '../../utils/formatters';

interface InternalUserSectionProps {
    users: InternalOverviewResponse['users'];
    subscriptions: InternalOverviewResponse['subscriptions'];
    periodLabel: string;
}

export const InternalUserSection: FC<InternalUserSectionProps> = ({ users, subscriptions, periodLabel }) => {
    const subscriptionsByPlanData = (subscriptions.byPlan || []).filter(p => p.count > 0).slice(0, 5);

    return (
        <div className="space-y-4">
            <section>
                <InternalSectionHeader
                    title="Users & Subscriptions"
                    description="User growth and subscription health monitoring"
                    icon={<Users className="w-5 h-5 text-blue-500" />}
                />
                <div className="grid grid-cols-5 gap-3">
                    <InternalKPICard label="Total Users" value={formatNumber(users.totalExternal ?? 0)} sublabelText="External" tooltip="Total count of registered external users." />
                    <InternalKPICard label={`New (${periodLabel})`} value={formatNumber(users.newInRange ?? 0)} sublabelText="Period" tooltip="New users registered in the selected period." />
                    <InternalKPICard label="Active Subs" value={formatNumber(subscriptions.active ?? 0)} sublabelText="Current" tooltip="Total number of currently active subscriptions." />
                    <InternalKPICard label="Past Due" value={formatNumber(subscriptions.pastDue ?? 0)} sublabelText="At risk" warn={subscriptions.pastDue > 0} tooltip="Subscriptions with failed payments." />
                    <InternalKPICard label="Canceled" value={formatNumber(subscriptions.canceled ?? 0)} sublabelText="Churned" tooltip="Total number of canceled subscriptions." />
                </div>
            </section>

            <section className="grid grid-cols-3 gap-3">
                {/* Recent Signups */}
                <InternalTableCard title="Recent Signups" action={{ label: 'View all', to: '/users' }} description="List of the most recent user registrations.">
                    {users.recentSignups.slice(0, 6).map((user) => (
                        <div key={user.id} className="flex items-center justify-between py-1.5">
                            <div className="flex items-center gap-2 min-w-0">
                                <div className="w-5 h-5 bg-neutral-100 flex items-center justify-center text-[9px] font-medium text-neutral-500 shrink-0">
                                    {user.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[11px] text-neutral-800 truncate">{user.name}</div>
                                </div>
                            </div>
                            <InternalStatusBadge status={user.status} />
                        </div>
                    ))}
                    {users.recentSignups.length === 0 && <p className="text-[11px] text-neutral-400 py-2">No signups</p>}
                </InternalTableCard>

                {/* Subscriptions by Plan */}
                <InternalChartCard title="Subscriptions by Plan" subtitle="Distribution" description="Distribution of users across different subscription plans.">
                    {subscriptionsByPlanData.length > 0 ? (
                        <div className="flex items-center gap-4">
                            <div className="w-24 h-24">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={subscriptionsByPlanData} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={20} outerRadius={40} strokeWidth={0}>
                                            {subscriptionsByPlanData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="flex-1 space-y-1.5">
                                {subscriptionsByPlanData.map((p, i) => (
                                    <div key={i} className="flex items-center justify-between text-[11px]">
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2 h-2" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                                            <span className="text-neutral-600 truncate max-w-[80px]">{p.name}</span>
                                        </span>
                                        <span className="font-medium text-neutral-900">{p.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : <p className="text-[11px] text-neutral-400">No data</p>}
                </InternalChartCard>

                {/* Subscription Status */}
                <InternalChartCard title="Subscription Health" subtitle="Current status breakdown" description="Breakdown of subscription statuses.">
                    <div className="space-y-3">
                        <InternalProgressRow label="Active" value={subscriptions.active} total={subscriptions.total} color="bg-neutral-800" />
                        <InternalProgressRow label="Trialing" value={subscriptions.trialing} total={subscriptions.total} color="bg-neutral-400" />
                        <InternalProgressRow label="Past Due" value={subscriptions.pastDue} total={subscriptions.total} color="bg-amber-500" />
                        <InternalProgressRow label="Canceled" value={subscriptions.canceled} total={subscriptions.total} color="bg-red-400" />
                    </div>
                </InternalChartCard>
            </section>
        </div>
    );
};
